// HerbalSoapHero.js - Herbal soap themed Hero section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHerbalSoapHeroElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'herbal-soap-hero-section';
  const heroContainerClass = 'herbal-soap-hero-container';
  const heroContentClass = 'herbal-soap-hero-content';
  const heroTitleClass = 'herbal-soap-hero-title';
  const heroHighlightClass = 'herbal-soap-hero-highlight';
  const heroSubtitleClass = 'herbal-soap-hero-subtitle';
  const heroButtonsClass = 'herbal-soap-hero-buttons';
  const heroPrimaryBtnClass = 'herbal-soap-hero-primary-btn';
  const heroSecondaryBtnClass = 'herbal-soap-hero-secondary-btn';
  const heroImageWrapperClass = 'herbal-soap-hero-image-wrapper';
  const heroImageClass = 'herbal-soap-hero-image';
  const heroOverlayClass = 'herbal-soap-hero-overlay';
  const heroPatternOverlayClass = 'herbal-soap-hero-pattern-overlay';
  const heroGradientOverlayClass = 'herbal-soap-hero-gradient-overlay';
  const heroStatsCtnClass = 'herbal-soap-hero-stats-container';
  const heroStatClass = 'herbal-soap-hero-stat';
  const heroStatNumberClass = 'herbal-soap-hero-stat-number';
  const heroStatLabelClass = 'herbal-soap-hero-stat-label';

  // Desktop Styles (default) - Enhanced herbal theme with natural organic background
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#1a3d1a',
    background: `
      radial-gradient(circle at 20% 80%, rgba(144, 238, 144, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 139, 34, 0.1) 0%, transparent 50%),
      linear-gradient(135deg, #1a3d1a 0%, #2d5a2d 25%, #228b22 50%, #2d5a2d 75%, #1a3d1a 100%)
    `,
    color: '#f8fafc',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    minHeight: '100vh'
  };

  const heroPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(144, 238, 144, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(34, 139, 34, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '60px 60px, 80px 80px',
    backgroundPosition: '0 0, 40px 40px',
    zIndex: '0',
    opacity: '0.6'
  };

  const heroGradientOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(45deg, transparent 0%, rgba(144, 238, 144, 0.05) 25%, transparent 50%, rgba(34, 139, 34, 0.05) 75%, transparent 100%),
      conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(144, 238, 144, 0.02) 90deg, transparent 180deg, rgba(34, 139, 34, 0.02) 270deg, transparent 360deg)
    `,
    zIndex: '0',
    opacity: '0.8'
  };

  const heroOverlayStyles = {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '60%',
    height: '100%',
    background: `
      radial-gradient(ellipse at 70% 50%, rgba(144, 238, 144, 0.12) 0%, rgba(34, 139, 34, 0.06) 40%, transparent 70%),
      linear-gradient(135deg, transparent 0%, rgba(144, 238, 144, 0.08) 50%, transparent 100%)
    `,
    zIndex: '0'
  };

  // Enhanced image styles with herbal-themed shadow and glow effect
  const heroImageStyles = {
    width: '100%',
    maxWidth: '600px',
    height: '700px',
    objectFit: 'cover',
    borderRadius: '12px',
    boxShadow: `
      0 30px 60px -12px rgba(0, 0, 0, 0.8),
      0 0 0 1px rgba(144, 238, 144, 0.3),
      0 0 40px rgba(144, 238, 144, 0.15)
    `,
    transition: 'all 0.6s ease',
    border: '3px solid #90EE90',
    filter: 'contrast(1.1) brightness(1.05)'
  };

  // Enhanced stats container with herbal-themed backdrop
  const heroStatsCtnStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '60px',
    padding: '32px',
    background: `
      linear-gradient(135deg, rgba(144, 238, 144, 0.12) 0%, rgba(34, 139, 34, 0.08) 100%),
      rgba(26, 61, 26, 0.8)
    `,
    borderRadius: '12px',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(144, 238, 144, 0.25)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(144, 238, 144, 0.1)
    `
  };

  // Enhanced button styles with herbal-themed gradients
  const heroPrimaryBtnStyles = {
    padding: '16px 40px',
    background: `
      linear-gradient(135deg, #90EE90 0%, #228b22 100%),
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)
    `,
    color: '#1a3d1a',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(144, 238, 144, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  };

  const heroSecondaryBtnStyles = {
    padding: '16px 40px',
    background: `
      linear-gradient(135deg, rgba(144, 238, 144, 0.1) 0%, rgba(34, 139, 34, 0.05) 100%),
      rgba(26, 61, 26, 0.3)
    `,
    color: '#f8fafc',
    border: '1px solid rgba(144, 238, 144, 0.4)',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: '"Inter", sans-serif',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
  };

  // Enhanced hover styles
  const heroPrimaryBtnHoverStyles = {
    background: `
      linear-gradient(135deg, #228b22 0%, #006400 100%),
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)
    `,
    transform: 'translateY(-3px)',
    boxShadow: `
      0 8px 25px rgba(144, 238, 144, 0.5),
      0 0 20px rgba(144, 238, 144, 0.3)
    `
  };

  const heroSecondaryBtnHoverStyles = {
    background: `
      linear-gradient(135deg, #90EE90 0%, #228b22 100%),
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)
    `,
    color: '#1a3d1a',
    transform: 'translateY(-3px)',
    boxShadow: `
      0 8px 25px rgba(144, 238, 144, 0.4),
      0 0 20px rgba(144, 238, 144, 0.2)
    `,
    border: '1px solid #90EE90'
  };

  // Add the missing styles
  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    gap: '80px',
    position: 'relative',
    zIndex: '1'
  };

  const heroContentStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: '650px',
    position: 'relative',
    zIndex: '2'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '64px',
    fontWeight: '700',
    color: '#f8fafc',
    lineHeight: '1.1',
    letterSpacing: '-0.02em',
    fontFamily: '"Inter", sans-serif'
  };

  const heroHighlightStyles = {
    color: '#90EE90',
    position: 'relative',
    display: 'inline-block',
    fontWeight: '700',
    background: `linear-gradient(135deg, #90EE90 0%, #228b22 100%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '22px',
    fontWeight: '400',
    color: '#cbd5e1',
    lineHeight: '1.7',
    maxWidth: '580px',
    fontFamily: '"Inter", sans-serif'
  };

  const heroButtonsStyles = {
    display: 'flex',
    gap: '20px',
    marginTop: '16px'
  };

  const heroImageWrapperStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
    position: 'relative',
    zIndex: '2'
  };

  const heroStatStyles = {
    textAlign: 'center',
    padding: '0 20px'
  };

  const heroStatNumberStyles = {
    fontSize: '42px',
    fontWeight: '700',
    color: '#90EE90',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const heroStatLabelStyles = {
    fontSize: '14px',
    color: '#cbd5e1',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    gap: '50px',
    padding: '0 30px'
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '52px'
  };

  const tabletHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '500px'
  };

  const tabletHeroImageStyles = {
    ...heroImageStyles,
    height: '600px'
  };

  const tabletHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    padding: '24px'
  };

  const tabletHeroStatNumberStyles = {
    ...heroStatNumberStyles,
    fontSize: '36px'
  };

  // Mobile Styles
  const mobileHeroSectionStyles = {
    ...heroSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px',
    minHeight: 'auto'
  };

  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    padding: '0 20px',
    gap: '50px'
  };

  const mobileHeroContentStyles = {
    ...heroContentStyles,
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '100%'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '42px'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '18px',
    textAlign: 'center'
  };

  const mobileHeroButtonsStyles = {
    ...heroButtonsStyles,
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  };

  const mobileHeroPrimaryBtnStyles = {
    ...heroPrimaryBtnStyles,
    width: '100%',
    maxWidth: '280px'
  };

  const mobileHeroSecondaryBtnStyles = {
    ...heroSecondaryBtnStyles,
    width: '100%',
    maxWidth: '280px'
  };

  const mobileHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '100%',
    order: '-1'
  };

  const mobileHeroImageStyles = {
    ...heroImageStyles,
    height: '400px',
    maxWidth: '100%'
  };

  const mobileHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    flexDirection: 'column',
    gap: '24px',
    padding: '24px'
  };

  const mobileHeroStatStyles = {
    ...heroStatStyles,
    padding: '12px 0'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContentClass, heroContentStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroHighlightClass, heroHighlightStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSecondaryBtnClass, heroSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageClass, heroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroOverlayClass, heroOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPatternOverlayClass, heroPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroGradientOverlayClass, heroGradientOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatsCtnClass, heroStatsCtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatClass, heroStatStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatNumberClass, heroStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatLabelClass, heroStatLabelStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${heroPrimaryBtnClass}--hover`, heroPrimaryBtnHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${heroSecondaryBtnClass}--hover`, heroSecondaryBtnHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroImageWrapperClass, tabletHeroImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(heroImageClass, tabletHeroImageStyles);
    newRegistry[DEVICES.TABLET].set(heroStatsCtnClass, tabletHeroStatsCtnStyles);
    newRegistry[DEVICES.TABLET].set(heroStatNumberClass, tabletHeroStatNumberStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroSectionClass, mobileHeroSectionStyles);
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroContentClass, mobileHeroContentStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonsClass, mobileHeroButtonsStyles);
    newRegistry[DEVICES.MOBILE].set(heroPrimaryBtnClass, mobileHeroPrimaryBtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroSecondaryBtnClass, mobileHeroSecondaryBtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageWrapperClass, mobileHeroImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageClass, mobileHeroImageStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatsCtnClass, mobileHeroStatsCtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatClass, mobileHeroStatStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroContainerClass,
    heroContentClass,
    heroTitleClass,
    heroHighlightClass,
    heroSubtitleClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroSecondaryBtnClass,
    heroImageWrapperClass,
    heroImageClass,
    heroOverlayClass,
    heroPatternOverlayClass,
    heroGradientOverlayClass,
    heroStatsCtnClass,
    heroStatClass,
    heroStatNumberClass,
    heroStatLabelClass
  ]));

  // Generate IDs for all elements
  const heroSectionId = Date.now();
  const containerID = Date.now() + 1;
  const contentID = Date.now() + 2;
  const titleID = Date.now() + 3;
  const highlightTextID = Date.now() + 4;
  const subtitleID = Date.now() + 5;
  const buttonsWrapperID = Date.now() + 6;
  const primaryButtonID = Date.now() + 7;
  const secondaryButtonID = Date.now() + 8;
  const imageWrapperID = Date.now() + 9;
  const heroImageID = Date.now() + 10;
  const overlayID = Date.now() + 11;
  const patternOverlayID = Date.now() + 12;
  const gradientOverlayID = Date.now() + 13;
  const statsContainerID = Date.now() + 14;
  const stat1ID = Date.now() + 15;
  const stat1NumberID = Date.now() + 16;
  const stat1LabelID = Date.now() + 17;
  const stat2ID = Date.now() + 18;
  const stat2NumberID = Date.now() + 19;
  const stat2LabelID = Date.now() + 20;
  const stat3ID = Date.now() + 21;
  const stat3NumberID = Date.now() + 22;
  const stat3LabelID = Date.now() + 23;

  const newElements = [
    // Main Hero Section Container
    {
      id: heroSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Hero Section',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [patternOverlayID, gradientOverlayID, overlayID, containerID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    
    // Pattern Overlay
    {
      id: patternOverlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pattern Overlay',
      parentId: heroSectionId,
      hierarchyName: "Pattern Overlay",
      className: heroPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-pattern-overlay'
      }
    },
    
    // Gradient Overlay
    {
      id: gradientOverlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gradient Overlay',
      parentId: heroSectionId,
      hierarchyName: "Gradient Overlay",
      className: heroGradientOverlayClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-gradient-overlay'
      }
    },
    
    // Background Overlay
    {
      id: overlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Background Overlay',
      parentId: heroSectionId,
      hierarchyName: "Background Overlay",
      className: heroOverlayClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-overlay'
      }
    },
    
    // Container for content and image
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: "Hero Container",
      className: heroContainerClass,
      styles: {},
      children: [contentID, imageWrapperID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-container'
      }
    },
    
    // Content area (left side)
    {
      id: contentID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Content',
      parentId: containerID,
      hierarchyName: "Hero Content",
      className: heroContentClass,
      styles: {},
      children: [titleID, subtitleID, buttonsWrapperID, statsContainerID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-content'
      }
    },
    
    // Title
    {
      id: titleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: contentID,
      hierarchyName: "Hero Title",
      className: heroTitleClass,
      styles: {},
      children: [highlightTextID],
      text: 'Nourish your skin with ',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-title'
      }
    },
    
    // Highlight Text Span
    {
      id: highlightTextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Highlight Text',
      parentId: titleID,
      hierarchyName: "Highlight Text",
      className: heroHighlightClass,
      styles: {},
      children: [],
      text: 'pure nature',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-highlight-text'
      }
    },
    
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: contentID,
      hierarchyName: "Hero Subtitle",
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'Discover our premium collection of handcrafted herbal soaps made with 100% organic ingredients. Experience the healing power of nature with our chemical-free, eco-friendly soap bars that cleanse, moisturize, and rejuvenate your skin naturally.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-subtitle'
      }
    },
    
    // Buttons Wrapper
    {
      id: buttonsWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Buttons',
      parentId: contentID,
      hierarchyName: "Hero Buttons",
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID, secondaryButtonID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-buttons'
      }
    },
    
    // Primary Button
    {
      id: primaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Primary Button",
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Shop Now',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-primary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      linkTo: 'order'
    },
    
    // Secondary Button
    {
      id: secondaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Secondary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Secondary Button",
      className: heroSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'Learn More',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-secondary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      linkTo: 'about'
    },
    
    // Stats Container
    {
      id: statsContainerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stats Container',
      parentId: contentID,
      hierarchyName: "Stats Container",
      className: heroStatsCtnClass,
      styles: {},
      children: [stat1ID, stat2ID, stat3ID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stats-container'
      }
    },
    
    // Stat 1
    {
      id: stat1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1',
      parentId: statsContainerID,
      hierarchyName: "Stat 1",
      className: heroStatClass,
      styles: {},
      children: [stat1NumberID, stat1LabelID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat'
      }
    },
    
    // Stat 1 Number
    {
      id: stat1NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Number',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '100%',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-number'
      }
    },
    
    // Stat 1 Label
    {
      id: stat1LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Label',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Organic',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-label'
      }
    },
    
    // Stat 2
    {
      id: stat2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2',
      parentId: statsContainerID,
      hierarchyName: "Stat 2",
      className: heroStatClass,
      styles: {},
      children: [stat2NumberID, stat2LabelID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat'
      }
    },
    
    // Stat 2 Number
    {
      id: stat2NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Number',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '50+',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-number'
      }
    },
    
    // Stat 2 Label
    {
      id: stat2LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Label',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Natural Ingredients',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-label'
      }
    },
    
    // Stat 3
    {
      id: stat3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3',
      parentId: statsContainerID,
      hierarchyName: "Stat 3",
      className: heroStatClass,
      styles: {},
      children: [stat3NumberID, stat3LabelID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat'
      }
    },
    
    // Stat 3 Number
    {
      id: stat3NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Number',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '5000+',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-number'
      }
    },
    
    // Stat 3 Label
    {
      id: stat3LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Label',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Happy Customers',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-stat-label'
      }
    },
    
    // Image Wrapper (right side)
    {
      id: imageWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Image Wrapper',
      parentId: containerID,
      hierarchyName: "Hero Image Wrapper",
      className: heroImageWrapperClass,
      styles: {},
      children: [heroImageID],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-hero-image-wrapper'
      }
    },
    
    // Hero Image
    {
      id: heroImageID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Hero Image',
      parentId: imageWrapperID,
      hierarchyName: "Hero Image",
      className: heroImageClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      src: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=2187&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'herbal-soap-hero-image',
        'alt': 'Natural herbal soap bars with organic ingredients'
      }
    }
  ];
  
  // Return all elements
  return {
    heroSection: newElements[0],
    elements: newElements
  };
}; 